import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import { SEOService } from "../services/SEOService";
import AppService from "../services/AppService";
import type { App } from "../types/app";
import type { PSEOTemplateReport } from "../types/seo";

const MODELS = [
  { value: "", label: "Default (Gemini 2.5 Flash Lite)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null}
  </div>
);

const ProgrammaticSEO: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [model, setModel] = useState("");
  const [templateCount, setTemplateCount] = useState(8);
  const [reports, setReports] = useState<PSEOTemplateReport[]>([]);
  const [currentReport, setCurrentReport] = useState<PSEOTemplateReport | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedApp = useMemo(
    () => apps.find((app) => app.appId === selectedAppId) || null,
    [apps, selectedAppId],
  );

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const appList = await AppService.getUserApps(user.email);
    setApps(appList);
  }, [user?.email]);

  const loadReports = useCallback(async () => {
    if (!user?.email || !selectedAppId) {
      setReports([]);
      return;
    }
    const items = await SEOService.getAppTemplateReports(
      user.email,
      selectedAppId,
    );
    setReports(items);
  }, [selectedAppId, user?.email]);

  useEffect(() => {
    loadApps().catch((err) => {
      console.error(err);
      setError("Failed to load apps.");
    });
  }, [loadApps]);

  useEffect(() => {
    loadReports().catch((err) => {
      console.error(err);
      setError("Failed to load saved pSEO template reports.");
    });
  }, [loadReports]);

  const handleSuggestTemplates = async () => {
    if (!user?.email || !selectedApp) {
      setError("Select a registered app first.");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const report = await SEOService.suggestPSEOTemplates({
        userId: user.email,
        app: selectedApp,
        count: templateCount,
        model: model || undefined,
      });
      setCurrentReport(report);
      setTab(1);
      setSuccess(
        `Generated ${report.templates.length} pSEO template suggestions.`,
      );
    } catch (suggestError: any) {
      console.error(suggestError);
      setError(suggestError?.message || "Failed to suggest pSEO templates.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!currentReport) return;
    setSaving(true);
    try {
      await SEOService.saveTemplateReport(currentReport);
      await loadReports();
      setSuccess("pSEO template report saved.");
    } catch (saveError: any) {
      console.error(saveError);
      setError(saveError?.message || "Failed to save pSEO report.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (report: PSEOTemplateReport) => {
    if (!user?.email) return;
    if (!window.confirm("Delete this pSEO template report?")) return;
    try {
      await SEOService.deleteTemplateReport(user.email, report.reportId);
      await loadReports();
      if (currentReport?.reportId === report.reportId) {
        setCurrentReport(null);
      }
      setSuccess("Report deleted.");
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete report.");
    }
  };

  const exportReport = (report: PSEOTemplateReport, format: "md" | "json") => {
    const content =
      format === "md"
        ? SEOService.exportReportAsMarkdown(report)
        : SEOService.exportReportAsJSON(report);
    const link = document.createElement("a");
    link.href = `data:${
      format === "md" ? "text/markdown" : "application/json"
    };charset=utf-8,${encodeURIComponent(content)}`;
    link.download = `${report.appName}-pseo-templates.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Programmatic SEO
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select a registered app and generate high-traffic long-term pSEO
        template suggestions with rationale and traffic potential.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Tab label="Generate Templates" />
        <Tab label="Current Report" />
        <Tab label="Saved Reports" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Select a registered app"
                value={selectedAppId}
                onChange={(event) => setSelectedAppId(event.target.value)}
              >
                <MenuItem value="">
                  <em>Select a registered app</em>
                </MenuItem>
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="number"
                fullWidth
                label="Template Count"
                inputProps={{ min: 1, max: 20 }}
                value={templateCount}
                onChange={(event) =>
                  setTemplateCount(
                    Math.max(1, Math.min(20, Number(event.target.value || 1))),
                  )
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Model (Optional)"
                value={model}
                onChange={(event) => setModel(event.target.value)}
              >
                {MODELS.map((option) => (
                  <MenuItem
                    key={option.value || "default"}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSuggestTemplates}
                disabled={loading || !selectedApp}
              >
                {loading ? "Generating..." : "Suggest pSEO Templates"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {!currentReport ? (
          <Alert severity="info">
            Generate suggestions first or open a saved report.
          </Alert>
        ) : (
          <Paper sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {currentReport.appName} pSEO Template Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Requested: {currentReport.requestedCount} | Model:{" "}
                  {currentReport.modelUsed}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportReport(currentReport, "md")}
                >
                  Markdown
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => exportReport(currentReport, "json")}
                >
                  JSON
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveReport}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Report"}
                </Button>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {currentReport.summary}
            </Typography>

            <Grid container spacing={2}>
              {currentReport.templates.map((item) => (
                <Grid item xs={12} md={6} key={item.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6">{item.templateName}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.8 }}>
                        <strong>Pattern:</strong> {item.templatePattern}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.8 }}>
                        <strong>Why suggested:</strong> {item.whySuggested}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.8 }}>
                        <strong>App relevance:</strong> {item.appRelevance}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={`Traffic Potential: ${item.trafficPotential.toUpperCase()}`}
                          size="small"
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.8, display: "block" }}
                      >
                        {item.trafficPotentialReason}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {reports.length === 0 ? (
          <Alert severity="info">
            No saved pSEO template reports for the selected app.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {reports.map((report) => (
              <Grid item xs={12} md={6} key={report.reportId}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{report.appName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(
                        report.savedAt || report.createdAt
                      ).toLocaleString?.() || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {report.summary}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Templates: {report.templates.length}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        setCurrentReport(report);
                        setTab(1);
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteReport(report)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>
    </Box>
  );
};

export default ProgrammaticSEO;
